import React, { useEffect, useState } from 'react';
import CheckBox from 'src/components/CheckBox';
import Radio from 'src/components/Radio/Radio';
import { getShortName } from 'src/utils/CommonFunctions';

export interface IAiQuestionOption {
    id: string | number;
    title: string;
    subtitle?: string;
    image?: string;
    rightSideValue?: React.ReactNode;
    content?: React.ReactNode;
}

interface IProps {
    options: IAiQuestionOption[];
    selectedIds?: (string | number)[];
    onChange?: (ids: (string | number)[]) => void;
    orderedList?: boolean;
    imageList?: boolean;
    checkBoxSelection?: boolean;
}

const OptionAvatar = ({ image, title, idx, isSelected, orderedList, imageList }: { image?: string; title: string; idx: number; isSelected: boolean; orderedList?: boolean; imageList?: boolean }) => {
    const [imgError, setImgError] = useState(false);

    if (orderedList) {
        return <span className={`table-profile-customer-circle-xs !text-[10px] group-hover:bg-primary group-hover:text-white ${isSelected ? '!bg-primary !text-white' : ''}`}>{idx + 1}</span>;
    }

    if (imageList) {
        if (image && !imgError) {
            return <img src={image} alt={title} className="table-profile-icn-circle-xs" onError={() => setImgError(true)} />;
        }

        return <div className="table-profile-customer-circle-xs">{getShortName(title)}</div>;
    }

    return null;
};

const AiQuestionSelection = ({ options, selectedIds = [], onChange, orderedList, imageList, checkBoxSelection }: IProps) => {
    const toggle = (id: string | number) => {
        if (!checkBoxSelection) {
            onChange?.([id]);

            return;
        }

        const next = selectedIds.includes(id) ? selectedIds.filter((s) => s !== id) : [...selectedIds, id];
        onChange?.(next);
    };

    // Arrow up/down cycles the single-select radio options; skipped for checkbox lists where
    // arrow keys have no natural "current selection" to move from.
    useEffect(() => {
        if (checkBoxSelection || !options.length) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;

            e.preventDefault();

            const currentIndex = options.findIndex((option) => selectedIds.includes(option.id));
            const delta = e.key === 'ArrowDown' ? 1 : -1;
            const nextIndex = (currentIndex + delta + options.length) % options.length;
            onChange?.([options[nextIndex].id]);
        };

        document.addEventListener('keydown', handleKeyDown);

        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [checkBoxSelection, options, selectedIds, onChange]);

    return (
        <ul className="rounded-lg border border-utilityGray200 shadow-xs divide-y divide-utilityGray200 overflow-hidden">
            {options.map((option, idx) => {
                const isSelected = selectedIds.includes(option.id);

                return (
                    <li key={option.id} className={`transition-colors duration-150 p-3 hover:bg-utilityGray100 group ${isSelected ? 'bg-utilityGray100' : 'bg-white'}`}>
                        <div onClick={() => toggle(option.id)} className="flex items-center gap-2 cursor-pointer ">
                            {/* Left: number badge (orderedList), image/short name fallback (imageList), or nothing */}
                            {(orderedList || imageList) && (
                                <div className="flex-shrink-0 ">
                                    <OptionAvatar image={option.image} title={option.title} idx={idx} isSelected={isSelected} orderedList={orderedList} imageList={imageList} />
                                </div>
                            )}

                            {/* Center: title + subtitle */}
                            <div className="flex-1 min-w-0">
                                <p className="text-textSecondary text-xs leading-[1.5] font-medium">{option.title}</p>
                                {option.subtitle && <p className="text-gray500 text-xs leading-[1.5] font-normal">{option.subtitle}</p>}
                            </div>

                            {/* Right: badge (optional) + checkbox/radio */}
                            {option.rightSideValue && option.rightSideValue}
                            <div className="flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                                {checkBoxSelection ? (
                                    <CheckBox id={`ai-question-option-${option.id}`} checked={isSelected} onChangeFunc={() => toggle(option.id)} />
                                ) : (
                                    <Radio inputName="ai-question-option" checked={isSelected} onChangeFunc={() => toggle(option.id)} />
                                )}
                            </div>
                        </div>

                        {/* Custom content (e.g. free-text input, height/weight fields) — animated open/close via grid-template-rows */}
                        {option.content && (
                            <div className="grid transition-[grid-template-rows] duration-300 ease-in-out" style={{ gridTemplateRows: isSelected ? '1fr' : '0fr' }}>
                                <div
                                    className={`overflow-hidden transition-opacity duration-300 ease-in-out [&>*]:mt-3 ${isSelected ? 'opacity-100 delay-100' : 'opacity-0'}`}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {option.content}
                                </div>
                            </div>
                        )}
                    </li>
                );
            })}
        </ul>
    );
};

export default AiQuestionSelection;
